import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChildren } from '@angular/core';

export interface TabsHeaderSubTab {
  label: string;
  virtual: boolean;
  zoneId: string | Array<string>;
}

export interface TabsHeaderTab {
  label: string;
  subTabs: Array<TabsHeaderSubTab>;
}

@Component({
  selector: 'app-tabs-header',
  templateUrl: './tabs-header.component.html',
  styleUrls: ['./tabs-header.component.scss'],
})
export class TabsHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input('tabs') public tabs: Array<TabsHeaderTab> = [];
  @Input('presentation') public presentation: boolean = false;
  @Input('showPrefix') public showPrefix: boolean = false;
  @Input('showSuffix') public showSuffix: boolean = false;
  @Input('prefixTemplate') public prefixTemplate: TemplateRef<any> = null;
  @Input('suffixTemplate') public suffixTemplate: TemplateRef<any> = null;
  @Input('upperTemplate') public upperTemplate: TemplateRef<any> = null;

  @Output('tabChange') public tabChangeEmitter = new EventEmitter<{ tab: TabsHeaderTab; tabIndex: number }>();
  @Output('subTabChange') public subTabChangeEmitter = new EventEmitter<{ tab: TabsHeaderSubTab; tabIndex: number }>();

  @ViewChildren('tab', { read: ElementRef }) public tabElements: QueryList<ElementRef>;
  @ViewChildren('subTab', { read: ElementRef }) public subTabElements: QueryList<ElementRef>;

  public subTabs: Array<TabsHeaderSubTab> = [];

  public activeTabIndex = 0;
  public activeSubTabIndex = 0;

  private presentationTimeout: any = null;
  private presentationSlideDuration: number = 30000;

  private stopPresentation() {
    if (!this.presentationTimeout) return;
    clearTimeout(this.presentationTimeout);
    this.presentationTimeout = null;
  }

  private startPresentation() {
    if (!this.presentation) return;
    this.stopPresentation();
    this.presentationTimeout = setTimeout(() => this.nextTab(), this.presentationSlideDuration);
  }

  private scrollToTabElement(elements: QueryList<ElementRef>, index: number, prevIndex: number) {
    elements.get(index)?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: prevIndex > index ? 'end' : 'start' });
  }

  public setTab(tabIndex: number) {
    const tab = this.tabs[tabIndex];
    if (!tab) return;

    this.subTabs = tab.subTabs || [];
    const prevTabIndex = this.activeTabIndex;

    this.activeTabIndex = tabIndex;
    this.scrollToTabElement(this.tabElements, tabIndex, prevTabIndex);
    this.startPresentation();
    this.tabChangeEmitter.emit({ tab, tabIndex: tabIndex });

    if (this.subTabs.length > 0) {
      this.setSubTab(0);
    }
  }

  public setSubTab(tabIndex: number) {
    const tab = this.subTabs[tabIndex];
    if (!tab) return;

    const prevTabIndex = this.activeSubTabIndex;

    this.activeSubTabIndex = tabIndex;
    this.scrollToTabElement(this.subTabElements, tabIndex, prevTabIndex);
    this.subTabChangeEmitter.emit({ tab, tabIndex: tabIndex });
  }

  public nextTab() {
    this.setTab((this.activeTabIndex + 1) % this.tabs.length);
  }

  public prevTab() {
    const prevTabIndex = (this.activeTabIndex - 1) % this.tabs.length;

    this.setTab(prevTabIndex < 0 ? this.tabs.length - 1 : prevTabIndex);
  }

  public nextSubTab() {
    this.setSubTab((this.activeSubTabIndex + 1) % this.subTabs.length);
  }

  public prevSubTab() {
    const prevTabIndex = (this.activeSubTabIndex - 1) % this.subTabs.length;

    this.setSubTab(prevTabIndex < 0 ? this.subTabs.length - 1 : prevTabIndex);
  }

  public ngOnInit(): void {
    this.startPresentation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs']) {
      this.activeTabIndex = 0;
      this.activeSubTabIndex = 0;
      this.subTabs = this.tabs[0]?.subTabs || [];
    }
    if (changes['presentation']) {
      this.stopPresentation();
      this.startPresentation();
    }
  }

  public ngOnDestroy(): void {
    this.stopPresentation();
  }

  public notVirtualFunc(tab: TabsHeaderSubTab): boolean {
    return !tab.virtual;
  }
}
